import { InlineDatePicker } from "components/DatePicker/InlineDatePicker";
import moment from "moment";
import React from "react";
import { Button, Message } from "semantic-ui-react";
import { LabelledRow } from "../components";
import NumberInput from "../components/NumberInput";
import { Reminder } from "DocumentReminder/models";

interface Props {
  reminders: Reminder[];
  existingReminder: Reminder;

  documentSendMoment: moment.Moment
  maxDaysAfterSendDate: number

  addReminder: (days: number) => Promise<any>
  updateReminder: (days: number, reminderNr: number) => Promise<any>
}

const EditReminder = (props: Props) => {
  const isNewReminder = props.existingReminder == null;
  const maxValue = props.maxDaysAfterSendDate;
  const sortedExistingReminders = [...props.reminders].sort((a, b) => a.days - b.days);
  const currentReminderIndex = sortedExistingReminders.findIndex(reminder => reminder.reminderNr === props.existingReminder?.reminderNr);

  // If we are editing, the lowest accepted value is the reminder before the one we edit's value + 1
  // If we are adding, the lowest accepted value is the final reminder's value + 1
  // If there is no previous reminder (we are adding or editing the first reminder), the lowest accepted value is 1
  const lowestAllowedDaysAfterSendDate = (isNewReminder
    ? sortedExistingReminders[sortedExistingReminders.length - 1]?.days ?? 0
    : sortedExistingReminders[currentReminderIndex - 1]?.days ?? 0) +
    1;

  const initialDaysAfterSendDate = lowestAllowedDaysAfterSendDate ? Math.min(maxValue, lowestAllowedDaysAfterSendDate + 3) : 3;

  const [reminderDay, setReminderDay] = React.useState(props.existingReminder ? props.existingReminder.days : initialDaysAfterSendDate);
  const [isSaving, setIsSaving] = React.useState(false);

  const selectedDate = props.documentSendMoment.clone().add(reminderDay, "days").startOf("day");

  // These dates are used to gray out dates in the date picker that are not valid
  const minDate = props.documentSendMoment.clone().add(1, "days").startOf("day");
  const maxDate = props.documentSendMoment.clone().add(maxValue, "days").startOf("day");

  const onDateSelected = (newDate: Date) => {
    const newDays = moment.utc(newDate).diff(props.documentSendMoment, "days");
    setReminderDay(newDays);
  };

  let error: string;

  if (reminderDay < lowestAllowedDaysAfterSendDate) {
    error = "This reminder occurs before the previous reminder. Please select a later date.";
  }

  const save = async () => {
    if (error || isSaving) {
      return;
    }

    setIsSaving(true);

    if (props.existingReminder != null) {
      await props.updateReminder(reminderDay, props.existingReminder.reminderNr);
    } else {
      await props.addReminder(reminderDay);
    }
  };

  return <div>
    <table className="s1_table bordergrid gray">
      <tbody>
        <LabelledRow label={RESX.DateTime.After}>
          <NumberInput
            value={reminderDay}
            onChanged={v => { setReminderDay(v.value); }}
            decimalPoints={0}
            canBeNegative={false}
            defaultWhenError={3}
            maxLength={2}
            minValue={1}
            maxValue={maxValue}
            width={38}
            labelContent={RESX.DateTime.days}
          />
        </LabelledRow>
        <LabelledRow label={RESX.DateTime.lblDate}>
          <InlineDatePicker
            value={selectedDate}
            minDate={minDate}
            maxDate={maxDate}
            onChange={m => onDateSelected(m.toDate())}
          />
        </LabelledRow>
      </tbody>
    </table>
    <div className="flex margin-top-bigger">
      <div className="grow1 margin-right">
        {error && <Message color="red">
          {error}
        </Message>}
      </div>
      <div>
        <Button onClick={save} primary disabled={isSaving} loading={isSaving}>
          {RESX.Buttons.btnSave}
        </Button>
      </div>
    </div>
  </div>;
};

export default EditReminder;